import React, { useContext, useEffect, useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import { Badge, Table } from "react-bootstrap";
import { LoaderContext } from "../../../../App";
import Pagination from "../../../../components/Pagination/Pagination";
import FormService from "../../../../core/service/FormService";
import { DEFAULT_ITEMS_PER_PAGE, STATUS } from "../../../../core/constants/common";
import "../Forms.scss";

function SummaryTab({ t, id, assessmentName, searchKey, filters = {}, activeTab }) {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [branchData, setBranchData] = useState([]);
  const [itemsTotalCount, setItemsTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const isInitialMount = useRef(true);
  const prevFiltersRef = useRef(null);
  const prevSearchKeyRef = useRef(null);
  const prevActiveTabRef = useRef(null);

  // Combine all effects into one to prevent duplicate calls
  useEffect(() => {
    if (!id) return;

    const isTabActive = activeTab === 'summary';
    const wasTabActive = prevActiveTabRef.current === 'summary';
    const tabBecameActive = !wasTabActive && isTabActive;

    // On initial mount or when tab becomes active, fetch data
    if (isInitialMount.current || tabBecameActive) {
      isInitialMount.current = false;
      prevActiveTabRef.current = activeTab;
      prevFiltersRef.current = filters.parentStatus;
      prevSearchKeyRef.current = searchKey;
      if (isTabActive) {
        getBranchSubmissions(0, itemsPerPage);
      }
      return;
    }

    // Update prevActiveTabRef even if tab is not active
    prevActiveTabRef.current = activeTab;

    // Only refetch if tab is active and filters or searchKey changed
    if (!isTabActive) return;

    // Check if filters or searchKey actually changed
    const filtersChanged = prevFiltersRef.current !== filters.parentStatus;
    const searchKeyChanged = prevSearchKeyRef.current !== searchKey;

    prevFiltersRef.current = filters.parentStatus;
    prevSearchKeyRef.current = searchKey;

    // Refetch only if something actually changed
    if (filtersChanged || searchKeyChanged) {
      setCurrentPage(1);
      getBranchSubmissions(0, itemsPerPage);
    }
  }, [id, searchKey, filters.parentStatus, activeTab]);

  const getBranchSubmissions = (pageNumber, itemsPerPageVal) => {
    setLoaderDisplay(true);
    let perPage = itemsPerPageVal || itemsPerPage;

    // Convert page number: API uses 0-based, but UI uses 1-based
    let apiPageNumber = pageNumber;
    if (pageNumber > 0) {
      apiPageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }

    // Build status filter from parentStatusFilters
    let statusParam = null;
    if (filters.parentStatus) {
      statusParam = filters.parentStatus;
    }

    FormService.getBranchSubmissions(id, apiPageNumber, perPage, statusParam)
      .then((response) => {
        console.log("Branch Submissions Response:", response.data);
        const totalCount = parseFloat(response.headers["x-total-count"] || 0);
        setItemsTotalCount(totalCount);
        setBranchData(response.data || []);
        setLoaderDisplay(false);
      })
      .catch((error) => {
        console.error("Error fetching branch submissions:", error);
        setLoaderDisplay(false);
      });
  };

  const calculateTotal = (statusCount) => {
    if (!statusCount) return 0;
    return Object.entries(statusCount).reduce((sum, [key, count]) => {
      // Exclude PENDING_APPROVAL from total calculation
      if (key === 'PENDING_APPROVAL' || key === 'REVOKED' || key === 'DUPLICATE' || key === 'EXPIRED') {
        return sum;
      }
      return sum + (count || 0);
    }, 0);
  };

  const renderStatusBadge = (item) => {
    const statusCount = item.submissionStatusCount || {};
    const completedCount = statusCount.COMPLETED || 0;
    const pendingApprovalLevel1 = statusCount.PENDING_APPROVAL_LEVEL_1 || 0;
    const pendingApprovalLevel2 = statusCount.PENDING_APPROVAL_LEVEL_2 || 0;
    const pendingApprovalLevel3 = statusCount.PENDING_APPROVAL_LEVEL_3 || 0;
    const totalPendingApproval = pendingApprovalLevel1 + pendingApprovalLevel2 + pendingApprovalLevel3;
    const inProgressCount = (statusCount.IN_PROGRESS || 0)
      + (statusCount.CORRECTION_PENDING || 0)
      + totalPendingApproval;
    const draftCount = statusCount.DRAFT || 0;
    const totalCount = calculateTotal(statusCount);

    // Completed Badge
    if (totalCount == completedCount) {
      return (
        <Badge bg="badge-Published" className="rounded-pill ms-2">
          {STATUS.COMPLETED}
        </Badge>
      );
    }
    // Not Started Badge
    else if (totalCount == draftCount) {
      return (
        <Badge bg="badge-New" className="rounded-pill ms-2">
          {STATUS.NOT_STARTED}
        </Badge>
      );
    }
    // In Progress Badge
    return (
      <Badge bg="badge-Draft" className="rounded-pill ms-2">
        {STATUS.IN_PROGRESS}
      </Badge>
    );
  };

  // Filter data based on search key
  const filteredData = branchData.filter((item) => {
    if (!searchKey) return true;
    const searchLower = searchKey.toLowerCase();
    return item.name?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="List_Table_01">
      <div className="card List_table-card01">
        <Table bordered responsive className="DCE_Summary_Table">
          <thead>
            <tr>
              <th rowSpan="2">{t("ParentEntity.label")}</th>
              <th className="text-center white-nowrap" rowSpan="2">{t("Total.label")}</th>
              <th className="text-center white-nowrap" rowSpan="2">{t("Completed.label")}</th>
              <th className="text-center white-nowrap" rowSpan="2">{t("InProgress.label")}</th>
              <th className="text-center white-nowrap" rowSpan="2">{t("NotStarted.label")}</th>
              {/* <th className="text-center white-nowrap" rowSpan="2">{t("NotShared.label")}</th> */}
              <th className="text-center white-nowrap" rowSpan="2">{t("CorrectionPending.label")}</th>
              <th className="text-center white-nowrap" colSpan="3">{t("PendingApproval.label") || "Pending Approval"}</th>
            </tr>
            <tr>
              <th className="text-center white-nowrap">1st Level</th>
              <th className="text-center white-nowrap">2nd Level</th>
              <th className="text-center white-nowrap">3rd Level</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => {
                const statusCount = item.submissionStatusCount || {};
                const total = calculateTotal(statusCount);

                // Extract all status counts
                const completed = statusCount.COMPLETED || 0;
                const inProgress = statusCount.IN_PROGRESS || 0;
                const draft = statusCount.DRAFT || 0;
                const revoked = statusCount.REVOKED || 0;
                const correctionPending = statusCount.CORRECTION_PENDING || 0;

                const pendingApprovalLevel1 = statusCount.PENDING_APPROVAL_LEVEL_1 || 0;
                const pendingApprovalLevel2 = statusCount.PENDING_APPROVAL_LEVEL_2 || 0;
                const pendingApprovalLevel3 = statusCount.PENDING_APPROVAL_LEVEL_3 || 0;

                return (
                  <tr key={item.id || index}>
                    <td className="white-nowrap">
                      <div className="d-flex align-items-center justify-content-between">
                        {item.name} {renderStatusBadge(item)}
                      </div>
                    </td>
                    <td className="text-center">{total}</td>
                    <td className="text-center">{completed}</td>
                    <td className="text-center">{inProgress}</td>
                    <td className="text-center">{draft}</td>
                    {/* <td className="text-center">{revoked}</td> */}
                    <td className="text-center">{correctionPending}</td>
                    <td className="text-center">{pendingApprovalLevel1}</td>
                    <td className="text-center">{pendingApprovalLevel2}</td>
                    <td className="text-center">{pendingApprovalLevel3}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="text-center">
                  <div className="Nodatafound_box">
                    <h6>{t("Nodatafound.label")}</h6>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {branchData.length > 0 && (
          <div className="p-2 List_footer_count">
            <Pagination
              itemsTotalCount={itemsTotalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              alwaysShown={true}
              setItemsPerPage={setItemsPerPage}
              pageClicked={(pageNum, perPage) => {
                getBranchSubmissions(pageNum, perPage);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default withTranslation()(SummaryTab);
